import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, CircularProgress } from "@mui/material";
import React from "react";
import { getSupportChatCategories } from "../Api";
import SupportChatCategoryDto from "../models/dtos/SupportChatCategoryDto";

interface CreateTicketModalProps {
    open: boolean,
    onClose: () => void,
    onSubmit: (chatName: string, message: string, categoryId: string) => void
}

export default function CreateTicketModal(props: CreateTicketModalProps) {
    const [chatName, setChatName] = React.useState<string>('');
    const [message, setMessage] = React.useState<string>('');
    const [categoryId, setCategoryId] = React.useState<string>('OTHER');
    const [categories, setCategories] = React.useState<SupportChatCategoryDto[]>([]);
    const [loadingCategories, setLoadingCategories] = React.useState<boolean>(false);
    const [errors, setErrors] = React.useState<{chatName?: string, message?: string}>({});

    React.useEffect(() => {
        if (props.open && categories.length === 0) {
            loadCategories();
        }
    }, [props.open]);

    const loadCategories = async () => {
        setLoadingCategories(true);
        try {
            const result = await getSupportChatCategories();
            if (result && result.length > 0) {
                setCategories(result);
                // Set the first category as default
                setCategoryId(result[0].id);
            }
        } catch (error) {
            console.error('Failed to load categories:', error);
        } finally {
            setLoadingCategories(false);
        }
    }

    const handleClose = () => {
        setChatName('');
        setMessage('');
        setCategoryId(categories.length > 0 ? categories[0].id : 'OTHER');
        setErrors({});
        props.onClose();
    }

    const validateForm = (): boolean => {
        const newErrors: {chatName?: string, message?: string} = {};
        
        if (chatName.length < 3) {
            newErrors.chatName = 'Ticket name must be at least 3 characters';
        } else if (chatName.length > 64) {
            newErrors.chatName = 'Ticket name must not exceed 64 characters';
        }
        
        if (message.length < 3) {
            newErrors.message = 'Message must be at least 3 characters';
        } else if (message.length > 1000) {
            newErrors.message = 'Message must not exceed 1000 characters';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const handleSubmit = () => {
        if (validateForm()) {
            props.onSubmit(chatName, message, categoryId);
            handleClose();
        }
    }

    return (
        <Dialog open={props.open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Create Support Ticket</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <TextField
                        label="Ticket Name"
                        value={chatName}
                        onChange={(e) => setChatName(e.target.value)}
                        error={!!errors.chatName}
                        helperText={errors.chatName || `${chatName.length}/64 characters`}
                        fullWidth
                        required
                        inputProps={{ maxLength: 64 }}
                    />
                    <TextField
                        label="Category"
                        value={categoryId}
                        onChange={(e) => setCategoryId(e.target.value)}
                        fullWidth
                        required
                        select
                        disabled={loadingCategories}
                    >
                        {loadingCategories ? (
                            <MenuItem disabled>
                                <CircularProgress size={20} />
                            </MenuItem>
                        ) : (
                            categories.map((cat) => (
                                <MenuItem key={cat.id} value={cat.id}>
                                    {cat.name}
                                </MenuItem>
                            ))
                        )}
                    </TextField>
                    <TextField
                        label="Message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        error={!!errors.message}
                        helperText={errors.message || `${message.length}/1000 characters`}
                        fullWidth
                        required
                        multiline
                        rows={4}
                        inputProps={{ maxLength: 1000 }}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="inherit">
                    Cancel
                </Button>
                <Button onClick={handleSubmit} variant="contained" color="primary">
                    Create Ticket
                </Button>
            </DialogActions>
        </Dialog>
    );
}
